import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { Link } from 'react-router-dom';


const AboutPage = () => {

  return (
    <>
      <Helmet>
        <title>Atlantis | About</title>
      </Helmet>
      <Header />
      <section className="px-5 py-10">
        <div className="container">
            <div className="row">
                <div className="col-sm-12 text-sm-start text-center">
                    <h1 className="display-5 fw-bold mb-4">About</h1>

                    <p className="fs-1 text-justify">
                      Characterizing the functional roles of individual amino acid residues is fundamental to
                      deciphering protein mechanisms and their implications for health and disease. To address this,
                      we developed Atlantis, a database that integrates structural, interaction, and functional
                      information at the human proteome residue level. A graph database enables complex queries
                      and the retrieval of integrated information for multiple functional analyses of protein systems.
                    </p>

                    <p className="fs-1 text-justify">
                      Atlantis annotates over 11M residues across 20K human proteins, identifying 888K intra-protein
                      contacts in 55K PDB structures and 786K inter-protein contacts in 11K PDB structures, along with
                      310K intra-protein contacts in 20K AlphaFold-predicted single-chain models. We also provide
                      the possibility to analyze and integrate predicted 3D complexes inputted by the user, and we
                      showcased these features on hundreds of AlphaFold-multimer complexes of GPCRs and LRRK2
                      interaction networks.
                    </p>

                    <p className="fs-1 mb-5 text-justify">
                      Beyond data integration, Atlantis offers active analytical capabilities through its
                      <strong><Link className="" to="/pathfinder/" target="_blank"> Pathfinder - Structural Graph Analysis</Link></strong> module
                      and the <strong><Link className="" to="/calculation/" target="_blank"> Contact Calculation</Link></strong> module.
                      The Pathfinder utilizes a Neo4j-powered Knowledge Graph to analyze protein structures as contact networks,
                      enriching topological analyses (such as shortest paths and centrality metrics) with cross-referencing of
                      variants, ligands, and PTMs. The Contact Calculation module extends this analytical framework to
                      user-provided structures, allowing researchers to upload custom experimental models or newly predicted
                      complexes to calculate residue contact networks and instantly map Atlantis's extensive functional
                      repository onto novel structures.
                    </p>

                 <h5 className="display-6 fw-bold mb-3">Data Sources</h5>
                <p className="fs-1 text-justify mb-2">
                  Atlantis integrates data from multiple curated sources to provide comprehensive residue-level annotations:
                </p>
                <ul className="mb-5">
                  <li className="fs-1 text-justify">
                    <strong>UniProtKB</strong> (Release v2026_01) — Sequence annotations including molecule processing, membrane topology, secondary structure, natural variants, binding pockets, active sites, disulfide bridges, and crosslinks. <em>(Evidence: Mixed – Curated Experimental & Computational Predictions)</em>
                  </li>
                  <li className="fs-1 text-justify">
                    <strong>InterPro</strong> (v108.0) and <strong>Pfam</strong> (v38.1) — Protein architecture, domain and family definitions. Sequences were aligned against the Pfam-A HMM library using <em>hmmalign</em> from the HMMER suite (v3.3.2). <em>(Evidence: Derived)</em>
                  </li>
                  <li className="fs-1 text-justify">
                    <strong>IntAct</strong> (v2026-01-14) — Experimentally verified protein–protein interaction binding regions. "Binding Region" column includes MI score reflecting evidence confidence in the Functional Site Information table. <em>(Evidence: Derived)</em>
                  </li>
                  <li className="fs-1 text-justify">
                    <strong>PhosphoSitePlus®</strong> (Release 04/2025) — High-confidence post-translational modifications (PTMs), including modification types and site positions. <em>(Evidence: Curated Experimental)</em>
                  </li>
                  <li className="fs-1 text-justify">
                    <strong>PDB</strong> — Experimental 3D structures for the human proteome. <em>(Evidence: Experimental)</em>
                  </li>
                  <li className="fs-1 text-justify">
                    <strong>AlphaFold Protein Structure Database</strong> (v6) — Predicted structures with pLDDT scores and secondary structure assignments. <em>(Evidence: Computational Prediction)</em>
                  </li>
                  <li className="fs-1 text-justify">
                    <strong>PDBe</strong> — Ligand metadata and physicochemical classifications (cofactor-like, drug-like, reactant-like, ion, small molecule). <em>(Evidence: Derived from Experimental PDBs)</em>
                  </li>
                  <li className="fs-1 text-justify">
                    <strong>ChEMBL</strong> — External cross-references for ligand data. Curated (literature-derived bioactivity data). <em>(Evidence: Curated Experimental)</em>
                  </li>
                  <li className="fs-1 text-justify">
                    <strong><a href="https://www.ncbi.nlm.nih.gov/clinvar/" target="_blank" rel="noopener noreferrer"><strong>ClinVar</strong></a></strong> (v2026-04-26) — Disease-associated genetic variants, annotated with AlphaMissense pathogenicity scores via the Ensembl Variant Effect Predictor (VEP). <em>(Evidence: Clinical Observation & Computational Prediction)</em>
                  </li>
                  <li className="fs-1 text-justify">
                    <strong><a href="https://alphamissense.hegelab.org/" target="_blank" rel="noopener noreferrer"><strong>AlphaMissense</strong></a></strong> —
                    AlphaMissense is a machine-learning model that predicts the likelihood of human missense genetic variants being pathogenic or benign by integrating amino acid evolutionary conservation with structural context from AlphaFold2.
                    Pathogenicity scores are calculated for each possible substitution, where higher values (red) indicate a greater probability of disease.
                    The average pathogenicity score (amMean) is visualized in the Feature Viewer and the Functional Site Information table.
                    Additionally, our in-house script provides extended metrics within the Functional Site Information table, including the most severe substitution (amMax), the least severe (amMin),
                    and the fraction of all 19 possible mutations classified as pathogenic (amPathogenicFrac, score ≥ 0.564), while specific ClinVar classifications are available in the ClinVar tab. <em>(Evidence: Computational Prediction)</em>
                  </li>
                </ul>

                    <h5 className="display-6 fw-bold mb-3">Methods</h5>

                    <p className="fs-1 text-justify mb-2">
                      <strong>Structural processing and contact definition: </strong>
                      Residue-residue contacts were defined using a Cβ distance cutoff of 8 Å (using Cα for Glycine).
                      Contacts were computed using an in-house pipeline implemented with cifparse-obj.
                      Residues from experimental structures were mapped to UniProtKB reference sequences via SIFTS XML integration.
                    </p>

                    <p className="fs-1 text-justify mb-2">
                      <strong>Ligand binding site mapping: </strong>
                      Ligand-binding sites within experimental PDB structures were spatially defined using a dedicated structural
                      processing pipeline. Residues were classified as part of the binding interface if the distance between any
                      ligand atom and the residue Cβ atom (or Cα for Glycine) was less than 8 Å. For each interaction, the specific
                      atom pair corresponding to the minimum distance was retained.
                    </p>

                    <p className="fs-1 text-justify mb-2">
                      <strong>Graph-based topological modeling: </strong>
                      The datasets were transformed into a graph architecture using Neo4j. The graph schema was designed to mirror
                      biological hierarchy while encoding 3D spatial relationships (i.e., 3D contacts) as traversable edges.
                      The graph was enriched by mapping functional features to their corresponding nodes.
                    </p>

                    <p className="fs-1 text-justify mb-5">
                      To maintain high-fidelity annotations, the Atlantis database undergoes synchronized annual updates
                      across all variant and functional annotation sources.
                    </p>

                    <img tabIndex="1" src="/assets/images/workflow.webp" className="center-help shrink img-fluid mb-5"
                     data-toggle="tooltip" data-placement="top" title="Click to zoom-in"/>

                    <h5 className="display-6 fw-bold mb-3">GraphQL API</h5>
                    <p className="fs-1 mb-5 text-justify mb-2">
                      Direct programmatic access to the Atlantis dataset is provided via a GraphQL API to facilitate
                      high-throughput analysis. Through the primary
                      endpoint (<a href="https://api.atlantis.bioinfolab.sns.it/graphql" target="_blank" rel="noopener noreferrer">https://api.atlantis.bioinfolab.sns.it/graphql</a>),
                      specific data layers—encompassing residue-level annotations, intra- and inter-chain structural contacts,
                      ligand interaction profiles, and ClinVar variants—can be retrieved. The
                      API documentation <Link className="" to="/api/" target="_blank"><b>page</b></Link> offers comprehensive information about
                      the API's functionality and supported methods, including schema definitions and syntax guidance,
                      to support integration into external computational pipelines, data analysis, and machine learning workflows.
                    </p>
                    <img tabIndex="1" src="/assets/images/graphql.webp" className="center-help shrink img-fluid mb-5"
                     data-toggle="tooltip" data-placement="top" title="Click to zoom-in"/>

                    <h5 className="display-6 fw-bold mb-3">Libraries</h5>
                    <ul className="mb-1 ml-10">
                        <li className="fs-1 text-justify">ReactJS (v18.2.0)</li>
                        <li className="fs-1 text-justify">Molstar (v4.1.0)</li>
                        <li className="fs-1 text-justify">PrimeReact (v10.5.0)</li>
                        <li className="fs-1 text-justify">React-Bootstrap (v2.10.0)</li>
                        <li className="fs-1 text-justify">FastAPI (v0.104.1)</li>
                        <li className="fs-1 text-justify">Strawberry-GraphQL (v0.217.1)</li>
                        <li className="fs-1 text-justify">SQLAlchemy (v1.4.51)</li>
                        <li className="fs-1 text-justify">Neo4j (v4.1.13)</li>
                        <li className="fs-1 text-justify">NetworkX (v3.4.2)</li>
                        <li className="fs-1 text-justify">Biopython (v1.83)</li>
                        <li className="fs-1 text-justify">Cifparse-obj (v7.105)</li>
                        <li className="fs-1 text-justify mb-4">BLASTP (v2.10.1+)</li>
                    </ul>

                    <h5 className="display-6 fw-bold mb-3">Citation</h5>
                    <p className="fs-1 text-justify mb-5">
                      De Oliveira Rosa, N.*, Ferronato, P.*, Matic, M., Varisco, M., Ruscio, M., Raimondi, F. &amp; Miglionico, P.
                      Atlantis: An integrative database for human proteome structural and functional sites.
                    </p>

                    <h5 className="display-6 fw-bold mb-3">Contact</h5>
                    <p className="fs-1 text-justify">Francesco Raimondi — <a href="mailto:francesco.raimondi@sns.it">francesco.raimondi@sns.it</a></p>
                    <p className="fs-1 text-justify">Pasquale Miglionico — <a href="mailto:pasquale.miglionico@sns.it">pasquale.miglionico@sns.it</a></p>
                    <p className="fs-1 text-justify mb-5">Natalia De Oliveira Rosa — <a href="mailto:natalia.deoliveirarosa@sns.it">natalia.deoliveirarosa@sns.it</a></p>

                    <h5 className="display-6 fw-bold mb-3">Source Code</h5>
                    <p className="fs-1 text-justify">
                      The underlying code is freely available at: <a href="https://github.com/raimondilab/atlantis_db" target="_blank" rel="noopener noreferrer">https://github.com/raimondilab/atlantis_db</a>
                    </p>
                </div>
            </div>
        </div>
</section>

      <Footer />
    </>
  );
};

export default AboutPage;